import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Field, FieldGrid, ToggleField } from "@/components/shared/form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input, NativeSelect } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ADDRESS_TYPES, INDIAN_STATES, labelFor } from "@/features/clients/constants";
import {
  useClientAddresses,
  useCreateAddress,
  useDeleteAddress,
  useUpdateAddress,
} from "@/features/clients/api";
import { useClientId } from "@/features/clients/detail/use-client-id";
import { HubToolbar } from "@/features/clients/detail/hub-toolbar";
import {
  addressFormSchema,
  emptyAddressForm,
  type AddressFormValues,
} from "@/features/clients/schema";
import type { ClientAddress } from "@/features/clients/types";

export function ClientAddressesPage() {
  const clientId = useClientId();
  const { data: addresses = [], isLoading } = useClientAddresses(clientId);
  const createAddress = useCreateAddress(clientId);
  const updateAddress = useUpdateAddress(clientId);
  const deleteAddress = useDeleteAddress(clientId);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ClientAddress | null>(null);
  const [deleting, setDeleting] = useState<ClientAddress | null>(null);

  const form = useForm<AddressFormValues>({
    resolver: zodResolver(addressFormSchema),
    defaultValues: emptyAddressForm,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(
      editing
        ? {
            title: editing.title,
            addr1: editing.addr1,
            addr2: editing.addr2 ?? "",
            city: editing.city,
            state: editing.state,
            pincode: editing.pincode,
            address_type: editing.address_type,
            is_default: editing.is_default,
            notes: editing.notes ?? "",
          }
        : emptyAddressForm,
    );
  }, [open, editing, form]);

  const errors = form.formState.errors;

  const onSubmit = form.handleSubmit(async (values) => {
    if (editing) await updateAddress.mutateAsync({ id: editing.id, values });
    else await createAddress.mutateAsync(values);
    setOpen(false);
    setEditing(null);
  });

  function startCreate() {
    setEditing(null);
    setOpen(true);
  }

  return (
    <div>
      <HubToolbar
        title="Addresses"
        description="Billing and plant locations used on invoices and delivery challans."
        action={
          <Button type="button" size="sm" onClick={startCreate}>
            <Plus />
            Add address
          </Button>
        }
      />

      <div className="p-4">
      {isLoading ? (
        <p className="py-8 text-center text-xs text-muted-foreground">Loading…</p>
      ) : addresses.length === 0 ? (
        <EmptyState
          title="No addresses yet"
          description="Add at least one billing address before raising an invoice."
          action={
            <Button type="button" size="sm" onClick={startCreate}>
              <Plus />
              Add address
            </Button>
          }
        />
      ) : (
        <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {addresses.map((address) => (
            <div
              key={address.id}
              className="group relative rounded-lg border border-border bg-card p-3 transition-colors hover:border-border-strong"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <MapPin className="size-3.5 shrink-0 text-muted-foreground" />
                    <span className="truncate text-xs font-medium">{address.title}</span>
                    <Badge variant="outline">{labelFor(ADDRESS_TYPES, address.address_type)}</Badge>
                    {address.is_default ? <Badge>Default</Badge> : null}
                  </div>
                  <address className="mt-1.5 text-xs not-italic leading-relaxed text-muted-foreground">
                    {address.addr1}
                    {address.addr2 ? `, ${address.addr2}` : ""}
                    <br />
                    {address.city}, {address.state} — {address.pincode}
                  </address>
                  {address.notes ? (
                    <p className="mt-1.5 text-2xs text-muted-foreground">{address.notes}</p>
                  ) : null}
                </div>
                <div className="flex shrink-0 gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Edit address"
                    onClick={() => {
                      setEditing(address);
                      setOpen(true);
                    }}
                  >
                    <Pencil />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label="Delete address"
                    className="hover:text-destructive"
                    onClick={() => setDeleting(address)}
                  >
                    <Trash2 />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      <Sheet
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setEditing(null);
        }}
      >
        <SheetContent>
          <SheetHeader>
            <SheetTitle>{editing ? "Edit address" : "New address"}</SheetTitle>
          </SheetHeader>

          <form id="address-form" onSubmit={onSubmit} className="flex-1 overflow-y-auto p-4">
            <FieldGrid>
              <Field label="Label" required span={7} error={errors.title?.message}>
                <Input {...form.register("title")} placeholder="Head office" autoFocus />
              </Field>
              <Field label="Type" span={5}>
                <NativeSelect {...form.register("address_type")}>
                  {ADDRESS_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="Address line 1" required span={12} error={errors.addr1?.message}>
                <Input {...form.register("addr1")} />
              </Field>
              <Field label="Address line 2" span={12}>
                <Input {...form.register("addr2")} />
              </Field>
              <Field label="City" required span={5} error={errors.city?.message}>
                <Input {...form.register("city")} />
              </Field>
              <Field label="State" required span={4} error={errors.state?.message}>
                <NativeSelect {...form.register("state")}>
                  <option value="">Select</option>
                  {INDIAN_STATES.map((s) => (
                    <option key={s.code} value={s.name}>
                      {s.name}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label="PIN" required span={3} error={errors.pincode?.message}>
                <Input maxLength={6} inputMode="numeric" {...form.register("pincode")} />
              </Field>
              <Field label="Notes" span={12} hint="Gate number, contact person at site">
                <Textarea rows={2} {...form.register("notes")} />
              </Field>
              <ToggleField
                label="Default for this type"
                span={12}
                checked={form.watch("is_default")}
                onChange={(v) => form.setValue("is_default", v, { shouldDirty: true })}
              />
            </FieldGrid>
          </form>

          <SheetFooter>
            <Button type="button" variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              form="address-form"
              size="sm"
              disabled={createAddress.isPending || updateAddress.isPending}
            >
              {editing ? "Save changes" : "Add address"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete address?"
        description={deleting ? `${deleting.title} will be removed.` : undefined}
        confirmLabel="Delete"
        destructive
        loading={deleteAddress.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          await deleteAddress.mutateAsync(deleting.id);
          setDeleting(null);
        }}
      />
    </div>
  );
}
